import Quill from "quill";
import Delta from "quill-delta";
import { Mention, MentionBlot } from "quill-mention";
import QuillTableBetter from "quill-table-better";
import "quill-mention/dist/quill.mention.css";
import "quill-table-better/dist/quill-table-better.css";
import "quill/dist/quill.snow.css";

const Parchment = Quill.import("parchment");

const DiffHighlightClass = new Parchment.Attributor(
  "diffHighlight",
  "data-diff",
  {
    scope: Parchment.Scope.INLINE_ATTRIBUTE,
  },
);

Quill.register(DiffHighlightClass, true);

const AnchorIdAttribute = new Parchment.Attributor("id", "id", {
  scope: Parchment.Scope.INLINE_ATTRIBUTE,
});

Quill.register(AnchorIdAttribute, true);
Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });

type Blot = Exclude<ReturnType<typeof Quill.find>, Quill | null>;

class TableBetter extends QuillTableBetter {
  constructor(...args: ConstructorParameters<typeof QuillTableBetter>) {
    super(...args);
    const { tableMenus } = this;
    const mergeCells = tableMenus.mergeCells.bind(tableMenus);
    tableMenus.mergeCells = () => {
      mergeCells();
      this.dropEmptyBlocksInMergedCell();
    };
  }

  private dropEmptyBlocksInMergedCell(): void {
    this.quill.update(Quill.sources.USER);

    const [cell] = this.cellSelection.selectedTds;
    if (!cell) return;

    const blocks = Array.from(cell.children)
      .map((node) => Quill.find(node))
      .filter((blot): blot is Blot => blot != null && !(blot instanceof Quill));
    const empty = blocks.filter((blot) => blot.length() === 1);
    const removable = empty.length === blocks.length ? empty.slice(1) : empty;
    if (!removable.length) return;

    removable.forEach((blot) => blot.remove());
    this.quill.update(Quill.sources.USER);
    this.cellSelection.setSelected(cell);
  }
}

QuillTableBetter.register();
Quill.register({ "modules/table-better": TableBetter }, true);

type ImageFormat = { sanitize: (url: string) => string };
const Image = Quill.import("formats/image") as ImageFormat;
const ALLOWED_IMAGE_PROTOCOLS = ["http", "https", "data", "blob"];
Image.sanitize = (url: string): string => {
  const a = document.createElement("a");
  a.href = url;
  const protocol = a.href.slice(0, a.href.indexOf(":"));
  return ALLOWED_IMAGE_PROTOCOLS.includes(protocol) ? url : "//:0";
};

/**
 * Table module options for the editor: the cell menu is what gives authors
 * border style/colour/width, background colour, padding and text alignment.
 */
export const EDITOR_TABLE_MODULE = {
  language: "en_US",
  menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
  toolbarTable: true,
  toolbarButtons: {
    whiteList: [
      "bold",
      "italic",
      "underline",
      "strike",
      "color",
      "background",
      "align",
      "list",
      "link",
    ],
    singleWhiteList: ["link"],
  },
};

export const replaceQuillContents = (quill: Quill, delta: Delta): void => {
  quill.deleteText(0, quill.getLength(), Quill.sources.SILENT);
  quill.updateContents(delta);
};

/**
 * Read-only viewers still need the module, because the clipboard matchers that
 * turn stored `<table>` markup back into table blots are registered by the
 * module's constructor. Without it, tables are dropped when the value is
 * converted. The editing UI is switched off.
 */
export const READ_ONLY_TABLE_MODULE = {
  language: "en_US",
  toolbarTable: false,
};

const TABLE_TEMPORARY_PATTERN = /<temporary\b[^>]*>[\s\S]*?<\/temporary>/gi;
const CELL_STATE_CLASS_PATTERN = /\s*\bql-cell-(?:focused|selected)\b/g;
const EMPTY_CLASS_PATTERN = /\sclass=""/g;

/**
 * Removes the editor-only markup that `quill-table-better` leaves in
 * `getSemanticHTML()` output, so none of it is ever saved
 */
export const stripEditorOnlyTableMarkup = (html: string): string =>
  html
    .replace(TABLE_TEMPORARY_PATTERN, "")
    .replace(CELL_STATE_CLASS_PATTERN, "")
    .replace(EMPTY_CLASS_PATTERN, "");

/**
 * Google Docs writes the footnote target as an `id`, which the attributor above
 * picks up on its own: Quill looks pasted attributes up by name. Word writes a
 * legacy `name` instead, so it needs this matcher.
 */
const matchAnchorName = (node: Node, delta: Delta): Delta => {
  const element = node as HTMLElement;
  const name = element.getAttribute("name");
  if (!name || element.getAttribute("id")) {
    return delta;
  }
  return delta.compose(new Delta().retain(delta.length(), { id: name }));
};

export const ANCHOR_CLIPBOARD_MATCHERS: [
  string,
  (node: Node, delta: Delta) => Delta,
][] = [["a[name]", matchAnchorName]];

export default Quill;
